import {Component, OnInit} from '@angular/core';
import {AngularFireMessaging} from '@angular/fire/compat/messaging';
import {Router} from '@angular/router';
import {TranslateService} from '@ngx-translate/core';
import {LanguageService} from '@core/language.service';
import {NotifierService} from 'angular-notifier';
import {JhiEventManager} from 'ng-jhipster';
import {LocalStorageService} from 'ngx-webstorage';
import {Subscription} from 'rxjs';
import {LoginService} from '@app/core';
import {NavItem} from '@shared/menu-list-item/nav-item';
import {INotification, Notification} from '@shared/model/notification.model';
import {NotificationService} from '@shared/services/notification.service';

@Component({
  selector: 'app-sidebar',
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss'],
})
export class SidebarComponent implements OnInit {
  // translate will be injected via constructor so component uses the app-wide TranslateService
  title = 'tienphong-ui';
  settingRoute = '/cai-dat-tai-khoan';
  notificationData: INotification;
  navItems: NavItem[] = [];
  private langSub?: Subscription;
  currentLang = 'vi';
  private langServiceSub?: Subscription;
  navItemsPermision: NavItem[] = [];
  permissionsSB = [];
  eventSubscriber: Subscription;
  constructor(
    public router: Router,
    private loginService: LoginService,
    private localStorage: LocalStorageService,
    private afMessaging: AngularFireMessaging,
    private notifierService: NotifierService,
    private notificationService: NotificationService,
    private eventManager: JhiEventManager,
    private translate: TranslateService,
    private languageService: LanguageService,
  ) {
    this.notificationData = new Notification();
    this.afMessaging.requestToken.subscribe(
      (token) => {
        this.notificationService.registerNotification(token).subscribe(() => {
          this.listen();
        });
      },
      (error) => {
        console.log(
          '%c The notification permission was not granted and blocked instead. ',
          'background: #222; color: #bada55',
          '(messaging/permission-blocked)',
        );
        // console.log(error);
      },
    );
  }

  ngOnInit() {
    this.getListNotification();
    this.changeNotification();

    // build the nav items using current language and rebuild on language change
    this.buildNavItems();
    this.langSub = this.translate.onLangChange.subscribe(() => {
      this.buildNavItems();
      // re-apply permission filtering so translated displayNames are used
      this.applyPermissions();
    });
    this.currentLang = localStorage.getItem('app_lang') || 'vi';
    this.langServiceSub = this.languageService.lang$.subscribe((l) => {
      this.currentLang = l;
    });
    const roleId = this.localStorage.retrieve('account.roleId');
    // apply permissions and build navItemsPermision using current navItems
    // wrapped in timeout to preserve original timing behavior
    setTimeout(() => this.applyPermissions(), 500);
  }

  ngOnDestroy(): void {
    if (this.langSub) {
      this.langSub.unsubscribe();
    }
    if (this.langServiceSub) {
      this.langServiceSub.unsubscribe();
    }
  }

  switchLang(lang: string) {
    this.languageService.changeLang(lang);
  }

  private applyPermissions(): void {
    this.navItemsPermision = [];
    const roleId = this.localStorage.retrieve('account.roleId');
    if (roleId) {
      const permissions = this.localStorage.retrieve('rules');
      if (permissions) {
        this.navItems.forEach((item: any) => {
          if (item.permission === 'can_report_monthly_activity') {
            if (
              permissions.includes('can_report_supplies') ||
              permissions.includes('can_report_activities')
            ) {
              this.navItemsPermision.push(item);
            }
          } else if (permissions.includes(item.permission)) {
            if (item.children && item.children.length > 0) {
              item.children = item.children.filter((child: any) => {
                return permissions.includes(child.permission);
              });
            }
            this.navItemsPermision.push(item);
          }
        });
      } else {
        this.navItemsPermision = this.navItems;
      }
    } else {
      this.navItemsPermision = this.navItems;
    }
  }

  private buildNavItems(): void {
    this.navItems = [
      {
        displayName: this.translate.instant(
          'layouts.sidebar.Dashboard_menu_link_text',
        ),
        iconName: 'dashboard',
        route: '/dashboard',
        permission: 'can_read_dashboard',
      },
      {
        displayName: this.translate.instant(
          'layouts.sidebar.Quản lý canh tác_menu_link_text',
        ),
        iconName: 'assignment',
        route: '/danh-sach-lo',
        permission: 'can_read_crops',
        activeRoutes: [
          '/chi-tiet-lo',
          '/chi-tiet-hanh-dong',
          '/chi-tiet-quy-trinh',
          '/danh-sach-cong-viec',
          '/lich-su-qr-code',
        ],
      },
      {
        displayName: this.translate.instant(
          'layouts.sidebar.Quản lý kho sản phẩm_menu_link_text',
        ),
        iconName: 'production_quantity_limits',
        route: '/danh-sach-san-pham',
        permission: 'can_list_product_block',
        activeRoutes: ['/chi-tiet-san-pham'],
      },
      {
        displayName: this.translate.instant(
          'layouts.sidebar.Quản lý thu hoạch_menu_link_text',
        ),
        iconName: 'chrome_reader_mode',
        route: '/danh-sach-thu-hoach',
        permission: 'can_read_harvests',
        activeRoutes: ['/chi-tiet-thu-hoach'],
      },
      {
        displayName: this.translate.instant(
          'layouts.sidebar.Quản lý code_menu_link_text',
        ),
        iconName: 'qr_code_2',
        route: '/danh-sach-qr-code',
        permission: 'can_read_crops',
        activeRoutes: ['/chi-tiet-qr-code'],
      },
      {
        displayName: this.translate.instant(
          'layouts.sidebar.Quản lý mẫu tem_menu_link_text',
        ),
        iconName: 'qr_code_2',
        route: '/danh-sach-mau-tem',
        permission: 'can_read_crops',
      },
      {
        displayName: this.translate.instant(
          'layouts.sidebar.Quản lý quy trình_menu_link_text',
        ),
        iconName: 'high_quality',
        route: '/danh-sach-tieu-chuan',
        permission: 'can_tbguidelines',
        activeRoutes: ['/chi-tiet-tieu-chuan'],
      },
      {
        displayName: this.translate.instant(
          'layouts.sidebar.Quản lý người dùng_menu_link_text',
        ),
        iconName: 'recent_actors',
        route: '/danh-sach-nguoi-dung',
        permission: 'can_read_user',
        activeRoutes: ['/phan-quyen-nguoi-dung'],
      },
      {
        displayName: this.translate.instant(
          'layouts.sidebar.Quản lý giống_menu_link_text',
        ),
        iconName: 'spa',
        route: '/danh-sach-giong',
        permission: 'can_list_giong',
        activeRoutes: ['/chi-tiet-giong'],
      },
      {
        displayName: this.translate.instant(
          'layouts.sidebar.Quản lý vật tư_menu_link_text',
        ),
        iconName: 'layers',
        route: '/danh-sach-vat-tu',
        permission: 'can_list_supplies',
        activeRoutes: ['/chi-tiet-vat-tu'],
      },
      {
        displayName: this.translate.instant(
          'layouts.sidebar.Báo cáo thống kê_menu_link_text',
        ),
        iconName: 'show_chart',
        route: '/bao-cao-thong-ke',
        permission: 'can_report_monthly_activity',
      },
      {
        displayName: this.translate.instant(
          'layouts.sidebar.Quản lý thiết bị_menu_link_text',
        ),
        iconName: 'build',
        route: '/danh-sach-thiet-bi',
        permission: 'can_list_devices',
      },
      {
        displayName: this.translate.instant(
          'layouts.sidebar.Quản lý đơn vị_menu_link_text',
        ),
        iconName: 'apartment',
        route: '/danh-sach-don-vi',
        permission: 'can_list_unit_managerment',
        activeRoutes: ['/chi-tiet-don-vi'],
      },
      {
        displayName: this.translate.instant(
          'layouts.sidebar.Quản lý hoạt động_menu_link_text',
        ),
        iconName: 'view_module',
        permission: 'can_read_activity_type',
        route: '/activity',
        activeRoutes: ['/chi-tiet-activity'],
      },
    ];
  }
  listen() {
    this.afMessaging.messages.subscribe((message: any) => {
      this.getListNotification();
    });
  }
  changeNotification() {
    this.eventSubscriber = this.eventManager.subscribe(
      'notificationModification',
      () => this.getListNotification(),
    );
  }
  getListNotification() {
    const params = {
      page: 0,
      size: 1,
      sort: ['sendDate,DESC'],
    };
    this.notificationService
      .getListNotification(params)
      .subscribe((res: any) => {
        this.notificationData = res.body ? res.body : null;
      });
  }
  logout() {
    this.notificationService.unRegisterNotification().subscribe(() => {
      this.loginService.logout();
      this.router.navigate(['/login']);
    });
  }
}
